import React from 'react';
import { Link } from 'react-router-dom';

const LINK_CLASS =
  'text-accent hover:text-teal-bright underline underline-offset-2 transition-colors';

function isInternalPath(url: string): boolean {
  return url.startsWith('/') && !url.startsWith('//');
}

function parseInlineMarkdown(text: string): React.ReactNode[] {
  const elements: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/^\*\*(.+?)\*\*/);
    if (boldMatch) {
      elements.push(
        <strong key={key++} className="font-semibold">
          {parseInlineMarkdown(boldMatch[1])}
        </strong>
      );
      remaining = remaining.slice(boldMatch[0].length);
      continue;
    }

    const italicMatch = remaining.match(/^\*(.+?)\*/);
    if (italicMatch) {
      elements.push(
        <em key={key++}>{parseInlineMarkdown(italicMatch[1])}</em>
      );
      remaining = remaining.slice(italicMatch[0].length);
      continue;
    }

    const codeMatch = remaining.match(/^`([^`]+)`/);
    if (codeMatch) {
      elements.push(
        <code key={key++} className="bg-background/50 px-1.5 py-0.5 rounded text-accent font-mono text-xs">
          {codeMatch[1]}
        </code>
      );
      remaining = remaining.slice(codeMatch[0].length);
      continue;
    }

    const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      const [, label, url] = linkMatch;
      if (isInternalPath(url)) {
        elements.push(
          <Link key={key++} to={url} className={LINK_CLASS}>
            {label}
          </Link>
        );
      } else {
        elements.push(
          <a
            key={key++}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className={LINK_CLASS}
          >
            {label}
          </a>
        );
      }
      remaining = remaining.slice(linkMatch[0].length);
      continue;
    }

    const nextSpecial = remaining.search(/\*\*|\*|`|\[/);
    if (nextSpecial === -1) {
      elements.push(remaining);
      break;
    } else if (nextSpecial === 0) {
      elements.push(remaining[0]);
      remaining = remaining.slice(1);
    } else {
      elements.push(remaining.slice(0, nextSpecial));
      remaining = remaining.slice(nextSpecial);
    }
  }

  return elements;
}

export function renderMarkdown(content: string): React.ReactNode {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let key = 0;
  let inCodeBlock = false;
  let codeBlockContent: string[] = [];
  let codeBlockLang = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith('```')) {
      if (!inCodeBlock) {
        inCodeBlock = true;
        codeBlockLang = line.slice(3).trim();
        codeBlockContent = [];
      } else {
        elements.push(
          <pre key={key++} className="bg-background/80 rounded-lg p-3 my-2 overflow-x-auto border border-border/50">
            <code className="text-xs font-mono text-text-secondary">
              {codeBlockContent.join('\n')}
            </code>
          </pre>
        );
        inCodeBlock = false;
        codeBlockContent = [];
        codeBlockLang = '';
      }
      continue;
    }

    if (inCodeBlock) {
      codeBlockContent.push(line);
      continue;
    }

    const numberedListMatch = line.match(/^(\d+)\.\s+(.+)/);
    if (numberedListMatch) {
      elements.push(
        <div key={key++} className="flex gap-2 my-1">
          <span className="text-accent font-medium min-w-[1.5rem]">{numberedListMatch[1]}.</span>
          <span>{parseInlineMarkdown(numberedListMatch[2])}</span>
        </div>
      );
      continue;
    }

    const bulletMatch = line.match(/^[-*]\s+(.+)/);
    if (bulletMatch) {
      elements.push(
        <div key={key++} className="flex gap-2 my-1">
          <span className="text-accent">•</span>
          <span>{parseInlineMarkdown(bulletMatch[1])}</span>
        </div>
      );
      continue;
    }

    const headingMatch = line.match(/^(#{1,3})\s+(.+)/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const headingClasses = level === 1
        ? 'text-lg font-bold mt-3 mb-2'
        : level === 2
          ? 'text-base font-semibold mt-2 mb-1'
          : 'text-sm font-medium mt-2 mb-1';
      elements.push(
        <div key={key++} className={headingClasses}>
          {parseInlineMarkdown(headingMatch[2])}
        </div>
      );
      continue;
    }

    if (line.trim() === '') {
      elements.push(<div key={key++} className="h-2" />);
      continue;
    }

    elements.push(
      <div key={key++}>
        {parseInlineMarkdown(line)}
      </div>
    );
  }

  if (inCodeBlock && codeBlockContent.length > 0) {
    elements.push(
      <pre key={key++} className="bg-background/80 rounded-lg p-3 my-2 overflow-x-auto border border-border/50">
        <code className="text-xs font-mono text-text-secondary">
          {codeBlockContent.join('\n')}
        </code>
      </pre>
    );
  }

  return <div className="space-y-0.5">{elements}</div>;
}
